'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

interface ReportFormData {
  reason: string;
  description: string;
}

const REPORT_REASONS = [
  { id: 'reason-inappropriate', label: 'Conteúdo inadequado' },
  { id: 'reason-spam', label: 'Spam' },
  { id: 'reason-copyright', label: 'Violação de copyright' },
  { id: 'reason-offensive', label: 'Conteúdo ofensivo' },
  { id: 'reason-other', label: 'Outro' },
];

interface ReportModalProps {
  onClose: () => void;
  gameTitle: string;
}

export default function ReportModal({ onClose, gameTitle }: ReportModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<ReportFormData>({
    defaultValues: { reason: 'reason-inappropriate' },
  });

  const onSubmit = async (data: ReportFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/games/:id/report with { reason, description }
    await new Promise((r) => setTimeout(r, 1200));
    toast.success('Denúncia enviada. A equipa de moderação irá analisar em breve.');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 shadow-2xl animate-scale-in">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-danger/15 flex items-center justify-center">
              <Icon name="FlagIcon" size={20} className="text-danger" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Denunciar jogo</h2>
              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{gameTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted/50 transition-colors">
            <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Motivo da denúncia</label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <label key={reason.id} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    value={reason.id}
                    className="w-4 h-4 text-primary border-border bg-input focus:ring-primary/50"
                    {...register('reason', { required: true })}
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {reason.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="report-desc" className="block text-sm font-medium text-foreground mb-1.5">
              Descrição adicional
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Opcional — fornece mais detalhes sobre a denúncia</p>
            <textarea
              id="report-desc"
              rows={3}
              placeholder="Descreve o problema com mais detalhe..."
              className="w-full px-4 py-2.5 rounded-xl bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
              {...register('description')}
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all border border-border"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-danger hover:bg-red-600 active:scale-95 transition-all duration-150 disabled:opacity-60"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <><Icon name="ArrowPathIcon" size={14} className="animate-spin" /> A enviar...</>
              ) : (
                <><Icon name="FlagIcon" size={14} /> Enviar denúncia</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}